import {AfterContentChecked, Component, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {IGuideStep, IGuideObject} from '@models/user-guide.model';
import {UserGuideService} from '@services/user-guide.service';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonComponent} from '../controls/button/button.component';

@Component({
  selector: 'app-user-guide',
  templateUrl: './user-guide.component.html',
  styleUrls: ['./user-guide.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonComponent
  ]
})
export class UserGuideComponent implements OnInit, AfterContentChecked {
  guideId: string;
  steps: IGuideStep[] = [];
  currentGuide: string;
  currentStep = 0;
  positioned: boolean;
  private prevGuide: string;

  @ViewChild('clipPath') clipPath;
  @ViewChild('infoBox') infoBox;

  constructor(
      protected userGuideService: UserGuideService,
  ) {
  }

  @HostListener('window:resize', [])
  _initClipPath() {
    if (!this.steps[this.currentStep]) {
      return;
    }
    const {element} = this.steps[this.currentStep];
    if (!element) {
      throw Error(`Element for guide ${this.currentGuide} has not been found`);
    }
    element.scrollIntoView({block: 'center'});
    this._drawClipPath();
  }

  @HostListener('window:scroll', [])
  _drawClipPath() {
    if (!this.steps.length || !this.infoBox) {
      return;
    }
    const {element, offsetX, offsetY, padding, noClip} = this.steps[this.currentStep];
    const {top, left, height, width} = element.getBoundingClientRect();
    const topWithOffset = Math.ceil(top) + (offsetY || 0);
    const leftWithOffset = Math.ceil(left) + (offsetX || 0);

    this.clipPath.nativeElement.style.clipPath = `polygon( evenodd,
      0 0, /* top - left */
      100% 0, /* top - right */
      100% 100%, /* bottom - right */
      0% 100%, /* bottom - left */
      0 0, /* and top - left again, then inner clip */
      ${leftWithOffset - (padding || 0)}px ${topWithOffset - (padding || 0)}px,
      ${leftWithOffset + Math.ceil(width) + (padding || 0)}px ${topWithOffset - (padding || 0)}px,
      ${leftWithOffset + Math.ceil(width) + (padding || 0)}px ${topWithOffset + Math.ceil(height) + (padding || 0)}px,
      ${leftWithOffset - (padding || 0)}px ${topWithOffset + Math.ceil(height) + (padding || 0)}px,
      ${leftWithOffset - (padding || 0)}px ${topWithOffset - (padding || 0)}px
    )`;

    if (noClip) {
      this.clipPath.nativeElement.classList = ['user-guide-overlay user-guide-overlay-transparent'];
    } else {
      this.clipPath.nativeElement.classList = ['user-guide-overlay'];
    }

    // has to be async, bc infobox content might change its size
    setTimeout(() => this._handleInfoBoxPosition(topWithOffset, leftWithOffset, height, width), 50);
    // this._handleInfoBoxPosition(topWithOffset, leftWithOffset, height, width);
  }

  /**
   * Will calculate where to show infobox by viewport size
   *  Calculates infobox position in order: right, left, bottom, top
   * @param topWithOffset top offset of guide element
   * @param leftWithOffset left offset of guide element
   * @param height height of clip path
   * @param width width of clip path
   */
  _handleInfoBoxPosition(topWithOffset: number, leftWithOffset: number, height: number, width: number) {
    const {nativeElement} = this.infoBox;
    let size = nativeElement.getBoundingClientRect();
    const {innerHeight, innerWidth} = window;
    const minWidth = size.width > 250 ? size.width : 250;
    // margin from clip path
    const offset = 18;
    let infoboxClassPostfix;
    let top;
    let left;
    let right;
    let transform = 'unset';

    // when switching between guides popover have to has only one postfix class
    [
      'user-guide-info-top',
      'user-guide-info-right',
      'user-guide-info-bottom',
      'user-guide-info-left'
    ].forEach((item) => nativeElement.classList.remove(item));

    // if (this.steps[this.currentStep].maxWidth) {
    //   nativeElement.style.maxWidth = this.steps[this.currentStep].maxWidth;
    // }

    const {placement} = this.steps[this.currentStep];
    if (
      placement === 'right' ||
      innerWidth > leftWithOffset + width + offset + minWidth &&  topWithOffset + height / 2 > size.height / 2
    ) {
      // right
      infoboxClassPostfix = 'right';
      top = topWithOffset + height / 2;
      left = leftWithOffset + width + offset;
      transform = 'translateY(-50%)';
    } else if (
      placement === 'left' ||
      leftWithOffset - offset - minWidth > 0 &&
      topWithOffset + height / 2 > size.height / 2
    ) {
      // left
      infoboxClassPostfix = 'left';
      top = topWithOffset + height / 2;
      left = leftWithOffset - minWidth - offset;
      transform = `translateY(-50%)`;
    } else if (
      placement === 'bottom' ||
      innerHeight > topWithOffset + height + offset + size.height
    ) {
      // bottom
      infoboxClassPostfix = 'bottom';
      top = topWithOffset + height + offset;
      left = leftWithOffset + (width / 2) - (size.width / 2);
    } else if (
      placement === 'top' ||
      topWithOffset - offset - size.height > 0
    ) {
      // top
      infoboxClassPostfix = 'top';
      top = topWithOffset - offset - size.height;
      left = leftWithOffset + (width / 2) - (size.width / 2);
    } else {
      // nothing fits, has to decide where it will be more space
      const rightSide = innerWidth - (leftWithOffset + width);
      const leftSide = leftWithOffset;
      const topSide = topWithOffset;
      const bottomSide = innerHeight - (topWithOffset + height);
      const sizes = [{
        side: 'right',
        size: rightSide,
      }, {
        side: 'left',
        size: leftSide,
      }, {
        side: 'top',
        size: topSide
      }, {
        side: 'bottom',
        size: bottomSide
      }];

      sizes.sort((item, nextItem) => {
        if (item.size > nextItem.size) {
          return -1;
        } else {
          return 1;
        }
      });

      const side = sizes[0];
      infoboxClassPostfix = side.side;
      switch (infoboxClassPostfix) {
        case 'right':
          top = topWithOffset + height / 2;
          left = leftWithOffset + width + offset;
          transform = 'translateY(-50%)';
          break;
        case 'left':
          top = topWithOffset + height / 2;
          left = leftWithOffset - minWidth - offset;
          transform = `translateY(-50%)`;
          break;
        case 'top':
          infoboxClassPostfix = 'top';
          top = topWithOffset - offset - size.height;
          left = leftWithOffset + (width / 2) - (minWidth / 2);
          break;
        case 'bottom':
          infoboxClassPostfix = 'bottom';
          top = topWithOffset + height + offset;
          left = leftWithOffset + (width / 2) - (minWidth / 2);
          break;
      }
    }

    if (['top', 'bottom'].includes(infoboxClassPostfix)) {
      // need to check for dimensions of viewport and infobox itself
      if (left < offset) {
        // if infobox will leave viewport, will align it by start of clip path
        left = leftWithOffset;
        const arrowLeft = width / 2;
        nativeElement.style.setProperty('--arrow-right', 'unset');
        nativeElement.style.setProperty('--arrow-left', `${arrowLeft}px`);
      } else if (left + minWidth > innerWidth) {
        // if infobox will leave viewport on the right side, we will align it to the end of clip path
        left = leftWithOffset + width - minWidth;
        if (left < offset) {
          left = undefined;
          right = innerWidth - (leftWithOffset + width);
        }
        const arrowRight = width / 2 - 8;
        nativeElement.style.setProperty('--arrow-left', 'unset');
        nativeElement.style.setProperty('--arrow-right', `${arrowRight}px`);
      } else {
        nativeElement.style.setProperty('--arrow-right', `unset`);
        nativeElement.style.setProperty('--arrow-left', '50%');
      }
    }

    nativeElement.style.transform = transform;
    nativeElement.style.top = top + 'px';
    nativeElement.style.left = left && left + 'px' || 'unset';
    nativeElement.style.right = right && right + 'px' || 'unset';
    nativeElement.classList.add(`user-guide-info-${infoboxClassPostfix}`);
    // add offsets to prevent infobox kiss sides of viewport
    nativeElement.style.marginLeft = 'unset';
    nativeElement.style.marginRight = 'unset';
    // size might already been changed, that's why taking one more time
    size = nativeElement.getBoundingClientRect();
    if (right && innerWidth - minWidth - right < offset) {
      nativeElement.style.marginLeft = offset + 'px';
    } else if (left && left + minWidth + offset > innerWidth) {
      nativeElement.style.marginRight = offset + 'px';
    }

    this.positioned = true;
  }

  ngOnInit() {
    // scroll restoration with guide might lead to clip path will be pointing on element hidden by top menu for instance
    // https://developer.chrome.com/blog/history-api-scroll-restoration/
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    this.userGuideService.activeGuide.subscribe((guideObject: IGuideObject) => {
      this.currentStep = 0;
      if (!guideObject) {
        this.steps = [];
        this.currentGuide = null;
        return;
      }

      const {guideId, steps} = guideObject;
      this.steps = steps;
      this.currentGuide = guideId;
      setTimeout(() => {
        this._initClipPath();
      }, 50);
    });

    this.userGuideService.guideUpdated.subscribe((guideObject: IGuideObject) => {
      if (this.currentGuide === guideObject.guideId) {
        this.steps = guideObject.steps;
      }
      this._initClipPath();
    });
    
    this.userGuideService.changeStep.subscribe((stepId) => {
      this.currentStep = stepId;
      this._initClipPath();
    });
  }

  ngAfterContentChecked() {
    if (this.currentGuide !== this.prevGuide) {
      this.prevGuide = this.currentGuide;
      if (this.currentGuide) {
        document.body.classList.add('user-guide-active');
      } else {
        document.body.classList.remove('user-guide-active');
      }
    }
  }

  /**
   * Shows next guide step
   * @param event dom event
   */
  nextButtonClick(event: Event) {
    event.stopPropagation();
    // if next step is the last step - should close the guide
    if (this.currentStep + 1 === this.steps.length) {
      this.hideGuide(true);
    } else {
      this.currentStep++;
      this._initClipPath();
    }
  }

  /**
   * Shows previous guide step
   * @param event dom event
   */
  backButtonClick(event: Event) {
    event.stopPropagation();
    this.currentStep--;
    this._initClipPath();
  }

  /**
   * Hides guide when user clicks skip | finish buttons
   *  Also hides when user clicks on overlay
   * @param force write to local storage, to prevent next page load showings (only true when buttons clicked)
   * @param event native event
   */
  hideGuide(force?: boolean, event?: Event) {
    if (event && event.target !== this.clipPath.nativeElement) {
      // only overlay click can hide user guide
      return;
    }
    // if (force) {
    //   this.userGuideService.done(this.currentGuide);
    // }
    this.userGuideService.hideGuide();
  }
}
